"use client";

import React, { useRef, useState } from "react";
import CityPicker from "@/components/CityPicker";
import PhoneInput from "@/components/PhoneInput";
import { useToast } from "@/components/ui/Toast";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

// Форма «Отправка документов»: ФИО, телефон, email, город + 4 ОТДЕЛЬНЫХ окна
// загрузки (паспорт главная/прописка, ВУ передняя/задняя) + 2 согласия.
// Десктоп — drag-and-drop, телефон — галерея/камера (нативный выбор).
// Отправка → /api/v1/documents → письмо городу (ссылки) + Telegram + сканы
// в 1С (AddDocs, реквизиты клиента не меняются) + карточка клиента в админке.
const ACCENT = "#0039A6";
const ACCEPT = ".pdf,image/*,application/pdf";
const MAX_TOTAL = 38 * 1024 * 1024; // ~38 МБ суммарно (бэк лимит 40)

type CityOpt = { station: string; name: string; slug?: string };

// 4 окна загрузки — ключи совпадают с полями формы на бэке.
const SLOTS = [
  { key: "passport_main", titleKey: "documents.slot.passport", subKey: "documents.slot.passportMainSub" },
  { key: "passport_reg", titleKey: "documents.slot.passport", subKey: "documents.slot.passportRegSub" },
  { key: "dl_front", titleKey: "documents.slot.dl", subKey: "documents.slot.dlFrontSub" },
  { key: "dl_back", titleKey: "documents.slot.dl", subKey: "documents.slot.dlBackSub" },
] as const;
type SlotKey = typeof SLOTS[number]["key"];

// Таиланд (Пхукет): те же 4 бэкенд-ключа, но другие надписи и порядок
// (ID-документ, ВУ перёд, ВУ зад, МВУ) — паспорт-прописка переиспользуется под IDP.
const TH_SLOTS: readonly { key: SlotKey; titleKey: string; subKey: string }[] = [
  { key: "passport_main", titleKey: "documents.slot.thId", subKey: "documents.slot.thIdSub" },
  { key: "dl_front", titleKey: "documents.slot.thDl", subKey: "documents.slot.thFrontSub" },
  { key: "dl_back", titleKey: "documents.slot.thDl", subKey: "documents.slot.thBackSub" },
  { key: "passport_reg", titleKey: "documents.slot.thIdp", subKey: "documents.slot.thIdpSub" },
];

// Одно окно загрузки: клик — выбор файла (на телефоне галерея/камера),
// перетаскивание файла — на десктопе; превью картинки, замена и удаление.
function UploadSlot({ title, sub, file, onFile }: {
  title: string; sub: string; file: File | null; onFile: (f: File | null) => void;
}) {
  const { t } = useLocale();
  const ref = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState("");
  const isImg = !!file && file.type.startsWith("image/");
  React.useEffect(() => {
    if (!file || !isImg) { setPreview(""); return; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file, isImg]);
  return (
    <div
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      style={{
        position: "relative", borderRadius: 12, cursor: "pointer", overflow: "hidden",
        border: file ? "1.5px solid #2BA55D" : `1.5px dashed ${drag ? ACCENT : "#B9C6E0"}`,
        background: drag ? "#EFF5FF" : file ? "#F6FBF8" : "#F8FAFE",
        minHeight: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 12, textAlign: "center", transition: "border-color .15s, background .15s",
      }}>
      <input ref={ref} type="file" accept={ACCEPT} style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); if (ref.current) ref.current.value = ""; }} />
      {file ? (
        <>
          {preview ? (
            <img src={preview} alt="" style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8 }} />
          ) : (
            <div style={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center", color: "#2BA55D", fontWeight: 700, fontSize: 13 }}>PDF</div>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: "#1E1E2D", fontWeight: 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
            <span style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>{t("common.replace")}</span>
            <span onClick={(e) => { e.stopPropagation(); onFile(null); }} style={{ fontSize: 12, color: "#d52b1e", fontWeight: 600 }}>{t("common.remove")}</span>
          </div>
        </>
      ) : (
        <>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" />
          </svg>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#1E1E2D" }}>{title}</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{sub}</div>
          <div style={{ fontSize: 11.5, color: "#98A0AE", marginTop: 6 }}>{t("documents.slotHint")}</div>
        </>
      )}
    </div>
  );
}

function Consent({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "#4A4A50", lineHeight: 1.4 }}>
      <button type="button" aria-pressed={checked} onClick={onToggle} style={{
        marginTop: 1, width: 18, height: 18, flexShrink: 0, cursor: "pointer", borderRadius: 5,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: checked ? `1px solid ${ACCENT}` : "1px solid #C9D3E6", background: checked ? ACCENT : "#fff",
      }}>
        {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </button>
      <span onClick={onToggle} style={{ cursor: "pointer" }}>{children}</span>
    </div>
  );
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} КБ`;
  return `${(b / 1024 / 1024).toFixed(1)} МБ`;
}

export default function DocumentsClient({ cities, defaultStation }: { cities: CityOpt[]; defaultStation: string }) {
  const { t, locale } = useLocale();
  const isEn = locale === "en";
  const toast = useToast();
  const [station, setStation] = useState(defaultStation);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("+7");
  const [email, setEmail] = useState("");
  const [slots, setSlots] = useState<Record<SlotKey, File | null>>({ passport_main: null, passport_reg: null, dl_front: null, dl_back: null });
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [sending, setSending] = useState(false);

  // Пхукет (Таиланд) → таиландский набор надписей + подсказка.
  const isThai = cities.find((c) => c.station === station)?.slug === "phuket";
  const activeSlots = isThai ? TH_SLOTS : SLOTS;

  const files = activeSlots.map((s) => slots[s.key]).filter(Boolean) as File[];
  const totalSize = files.reduce((s, f) => s + f.size, 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    if (!name.trim() || !email.trim()) { toast.show(t("documents.fillFioEmail"), "error"); return; }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 7) { toast.show(t("documents.phoneRequired"), "error"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.show(t("form.invalidEmail"), "error"); return; }
    if (!station) { toast.show(t("form.selectCity"), "error"); return; }
    const missing = activeSlots.filter((sl) => !slots[sl.key]);
    if (missing.length > 0) { toast.show(`${t("documents.addMissing")} ${missing.map((m) => `${t(m.titleKey)} (${t(m.subKey)})`).join(", ")}`, "error"); return; }
    if (totalSize > MAX_TOTAL) { toast.show(t("documents.tooBig"), "error"); return; }
    if (!agreePrivacy || !agreePersonal) { toast.show(t("documents.consentRequired"), "error"); return; }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("station", station);
      fd.append("name", name);
      fd.append("phone", `${dial} ${phone}`.trim());
      fd.append("email", email);
      for (const sl of activeSlots) { const f = slots[sl.key]; if (f) fd.append(sl.key, f, f.name); }
      const res = await fetch("/api/v1/documents", { method: "POST", body: fd });
      if (!res.ok) throw new Error("failed");
      track("documents_submit", { city: station });
      toast.show(t("documents.sent"), "success");
      setName(""); setPhone(""); setEmail("");
      setSlots({ passport_main: null, passport_reg: null, dl_front: null, dl_back: null });
      setAgreePrivacy(false); setAgreePersonal(false);
    } catch {
      toast.show(t("form.sendError"), "error");
    } finally {
      setSending(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 16px", border: "1px solid #E0E0E5", borderRadius: 10,
    fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "inherit", background: "#fff",
  };
  const label: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "#8E8E93", marginBottom: 6 };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "60px 20px 85px" }}>
      <h1 style={{ fontSize: 36, fontWeight: "bold", color: "#000", marginBottom: 0 }}>{t("documents.title")}</h1>
      <div style={{ width: 80, height: 3, background: ACCENT, margin: "20px 0 12px" }} />
      <p style={{ fontSize: 16, color: "#444", lineHeight: 1.6, marginBottom: 32 }}>
        {isEn ? "Fill in the form and attach your documents (PDF or photo). We'll receive them at the selected city's email and contact you if needed." : "Заполните форму и прикрепите документы (PDF или фото). Мы получим их на почту выбранного города и при необходимости свяжемся с вами."}
      </p>

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={label}>{t("form.city")} *</label>
          <CityPicker cities={cities} station={station} onPick={(st) => setStation(st)} inputStyle={inputStyle} />
        </div>
        <div>
          <label style={label}>{t("documents.fio")} *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("documents.fioPlaceholder")} style={inputStyle} />
        </div>
        <div>
          <label style={label}>{t("form.phone")} *</label>
          <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setDial(d); }} height={48} />
        </div>
        <div>
          <label style={label}>Email *</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@mail.ru" inputMode="email" style={inputStyle} />
        </div>

        {/* 4 окна загрузки: паспорт (главная/прописка) и ВУ (перёд/зад) */}
        <div>
          <label style={label}>{t("documents.photosLabel")} *</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12, gridAutoRows: "1fr" }}>
            {activeSlots.map((sl) => (
              <UploadSlot key={sl.key} title={t(sl.titleKey)} sub={t(sl.subKey)} file={slots[sl.key]}
                onFile={(f) => { if (f) track("documents_attach", { city: station }); setSlots((prev) => ({ ...prev, [sl.key]: f })); }} />
            ))}
          </div>
          {isThai && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#5A6472", lineHeight: 1.5, background: "#F5F8FF", border: "1px solid #DCE6FA", borderRadius: 10, padding: "10px 12px" }}>
              <div>{t("documents.thHint1")}</div>
              <div>{t("documents.thHint2")}</div>
            </div>
          )}
          {files.length > 0 && <div style={{ marginTop: 8, fontSize: 12, color: "#8E8E93" }}>{t("documents.total")} {fmtSize(totalSize)} {t("documents.max")}</div>}
        </div>

        <Consent checked={agreePrivacy} onToggle={() => setAgreePrivacy(!agreePrivacy)}>
          {t("consent.privacyPrefix")}{" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.privacy")}</a>
        </Consent>
        <Consent checked={agreePersonal} onToggle={() => setAgreePersonal(!agreePersonal)}>
          {t("consent.personalPrefix")}{" "}
          <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.personalData")}</a>
        </Consent>

        <button type="submit" disabled={sending} style={{
          marginTop: 8, height: 52, borderRadius: 10, border: "none", background: ACCENT, color: "#fff",
          fontSize: 16, fontWeight: 700, cursor: sending ? "default" : "pointer", opacity: sending ? 0.6 : 1, fontFamily: "inherit",
        }}>
          {sending ? t("form.sending") : t("form.send")}
        </button>
      </form>
    </div>
  );
}
